import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Send,
  User,
  Trash2,
  FileText,
  Upload,
  X,
  CreditCard,
  ChevronDown,
  LogOut,
  Menu,
  FolderOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  File,
  Target,
  Lightbulb,
  MessageSquare,
  CircleChevronLeft,
  RotateCcw
} from "lucide-react";
import peppaLogo from "../../assets/icons/peppa_logo_blue.svg";
import { useUser } from "../../context/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { encodeIdLong as encodeId, decodeIdLong as decodeId } from "../../lib/urlId";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../../components/auth/Service";
import CopilotService from "./CopilotService";
import { toast } from "sonner";
import { trackEvent, trackOncePerSession } from "../../lib/matomo";
import { buildLoginUrlFromLocation } from "../../lib/authRedirect";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { ThemeToggle } from "../../components/ui/theme-toggle";
import { Skeleton } from "../../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
// import creditsIcon from "../../assets/icons/peppa_coin.png";

import MessageBubble from "../../components/chat/MessageBubble";
import TypingIndicator from "../../components/chat/TypingIndicator";

export default function CopilotModern() {
  // États principaux
  const [spaces, setSpaces] = useState([]);
  const [activeSpace, setActiveSpace] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [pricing, setPricing] = useState({ credit_per_doc: 1, credit_per_mb: 0, multiplier: 1 });
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  // États pour la création d'espace
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // États pour les confirmations de suppression
  const [showDeleteSpaceDialog, setShowDeleteSpaceDialog] = useState(false);
  const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);


  // Gestion de la sidebar
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const isMobile = window.innerWidth < 1024;
    if (isMobile) return false;
    const savedState = localStorage.getItem('chat-sidebar-open');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  const { user, resetUserInfo, updateUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { spaceId } = useParams();

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [showMobileDocsPanel, setShowMobileDocsPanel] = useState(false);
  const isFetchingSpaceRef = useRef(false);
  const lastLoadedSpaceIdRef = useRef(null);

  // Sauvegarder l'état de la sidebar
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) {
      localStorage.setItem('chat-sidebar-open', JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen]);

  // Charger la tarification runtime
  useEffect(() => {
    CopilotService.getPricing()
      .then(res => setPricing(res.data))
      .catch(() => { });
  }, []);

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        setSidebarOpen(false);
      } else {
        const savedState = localStorage.getItem('chat-sidebar-open');
        setSidebarOpen(savedState !== null ? JSON.parse(savedState) : true);
      }
      setIsMobile(isMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize du textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  // Charger les espaces depuis l'API
  const fetchSpaces = useCallback(async () => {
    try {
      const res = await CopilotService.getSpaces();
      setSpaces(res.data);
    } catch (err) {
      console.error("Erreur lors du chargement des espaces", err);
    } finally {
      setSpacesLoaded(true);
    }
  }, []);

  // Charger la liste des espaces au montage uniquement
  useEffect(() => {
    setSpacesLoaded(false);
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    trackOncePerSession("copilot_opened", () => trackEvent("copilot", "copilot_opened"));
  }, []);

  useEffect(() => {
    const loadSpace = async () => {
      if (!spaceId) return;
      const decoded = decodeId(spaceId);
      if (!decoded) return;
      if (lastLoadedSpaceIdRef.current === decoded && activeSpace?.id === decoded) return;
      if (isFetchingSpaceRef.current) return;
      isFetchingSpaceRef.current = true;
      setMessagesLoaded(false);
      try {
        const spaceRes = await CopilotService.getSpace(decoded);
        const fullSpace = spaceRes.data;
        setActiveSpace(fullSpace);
        const messagesRes = await CopilotService.getMessages(decoded);
        const messages = messagesRes.data.map(msg => ({
          sender: msg.role,
          text: msg.message,
          timestamp: msg.timestamp,
          usesContext: msg.uses_context
        }));
        setMessages(messages);
        lastLoadedSpaceIdRef.current = decoded;
      } catch (err) {
        console.error("Erreur lors du chargement de l'espace", err);
      } finally {
        setMessagesLoaded(true);
        isFetchingSpaceRef.current = false;
      }
    };
    loadSpace();
  }, [spaceId, activeSpace?.id]);

  // Gestion des fichiers uploadés
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      status: 'pending' // pending, analyzing, analyzed, error
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };


  // const handleDeleteDocument = async (documentId) => {
  //   if (!activeSpace) return;

  //   try {
  //     await CopilotService.deleteDocument(documentId);

  //     // Supprimer visuellement le document immédiatement
  //     setActiveSpace(prev => ({
  //       ...prev,
  //       documents: prev.documents.filter(doc => doc.id !== documentId)
  //     }));

  //     toast.success("Document supprimé avec succès");

  //     // Recharger l'espace pour mettre à jour les données
  //     const spaceRes = await CopilotService.getSpace(activeSpace.id);
  //     setActiveSpace(spaceRes.data);
  //   } catch (err) {
  //     console.error("Erreur lors de la suppression du document", err);
  //     toast.error("Erreur lors de la suppression du document");
  //   }
  // };

  // Calculer le coût estimé (par doc + par MB) avec multiplicateur (runtime)
  const calculateCost = () => {
    const totalDocs = uploadedFiles.length;
    const totalSizeMB = uploadedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
    const raw = (pricing.credit_per_doc * totalDocs) + (pricing.credit_per_mb * totalSizeMB);
    return Math.ceil(raw * pricing.multiplier);
  };

  // Rafraîchir les crédits comme dans ChatModern
  const refreshCredits = async () => {
    try {
      await updateUser?.();
      setTimeout(() => {
        try { updateUser?.(); } catch (_) { }
      }, 800);
    } catch (_) { }
  };

  // Créer un nouvel espace
  const handleCreateSpace = async () => {
    if (!newSpaceName.trim()) {
      toast.error("Veuillez nommer l'espace");
      return;
    }

    // Vérification crédits (coût estimé basé sur les fichiers sélectionnés)
    const needed = calculateCost();
    if ((user?.credits ?? 0) < needed) {
      toast.error("Crédits insuffisants.");
      setShowCreditsDialog(true);
      try { trackEvent("copilot", "insufficient_credits_create_space", undefined, needed); } catch (_) { }
      return;
    }

    setIsLoading(true);

    try {
      const res = await CopilotService.createSpace(newSpaceName);
      const newSpace = res.data;

      // Fermer le dialog immédiatement
      setShowCreateDialog(false);
      setNewSpaceName("");
      const filesToUpload = [...uploadedFiles];
      setUploadedFiles([]);

      // Récupérer l'espace complet avec documents
      const spaceRes = await CopilotService.getSpace(newSpace.id);
      setActiveSpace(spaceRes.data);
      navigate(`/copilot/${encodeId(newSpace.id)}`, { replace: true });
      setMessages([]);
      setMessagesLoaded(true);

      // Recharger la liste des espaces
      await fetchSpaces();

      toast.success("Espace créé avec succès !");
      try { trackEvent("copilot", "space_created"); } catch (_) { }

      // Upload des documents en arrière-plan si présents
      if (filesToUpload.length > 0) {
        toast.info(`Début de l'analyse de ${filesToUpload.length} document(s)...`);
        try { trackEvent("copilot", "upload_started", undefined, filesToUpload.length); } catch (_) { }

        // Ajouter les documents en cours d'analyse à l'espace actif
        const analyzingDocuments = filesToUpload.map(file => ({
          id: `temp-${Date.now()}-${Math.random()}`,
          original_filename: file.name,
          file_size: file.size,
          status: 'analyzing',
          created_at: new Date().toISOString()
        }));

        setActiveSpace(prev => ({
          ...prev,
          documents: [...(prev.documents || []), ...analyzingDocuments]
        }));

        filesToUpload.forEach((file) => {
          CopilotService.uploadDocument(newSpace.id, file.file)
            .then((result) => {
              toast.success(`${file.name} analysé avec succès !`);
              try { trackEvent("copilot", "doc_uploaded"); } catch (_) { }
            })
            .catch((err) => {
              console.error(`Erreur lors de l'upload de ${file.name}:`, err);
              toast.error(`Erreur lors de l'analyse de ${file.name}`);
              try { trackEvent("copilot", "doc_upload_error"); } catch (_) { }
            });
        });

        // Recharger l'espace après un délai
        setTimeout(async () => {
          try {
            const updatedSpaceRes = await CopilotService.getSpace(newSpace.id);
            setActiveSpace(updatedSpaceRes.data);
            await refreshCredits();
          } catch (err) {
            console.error("Erreur lors du rechargement de l'espace", err);
          }
        }, 3000);
      }
    } catch (err) {
      console.error("Erreur lors de la création de l'espace", err);
      if (err?.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Erreur lors de la création de l'espace");
      }
      try { trackEvent("copilot", "space_create_error"); } catch (_) { }
    } finally {
      setIsLoading(false);
    }
  };

  // Envoyer un message (le backend calcule dynamiquement le coût, comme ChatModern)
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !activeSpace) return;

    // Chaque interaction utilise 1 crédit
    if ((user?.credits ?? 0) <= 0) {
      toast.error("Crédits insuffisants.");
      setShowCreditsDialog(true);
      try { trackEvent("copilot", "send_blocked_no_credits"); } catch (_) { }
      return;
    }

    setIsLoading(true);
    setIsTyping(true);

    const userMessage = {
      sender: "user",
      text: inputValue,
      timestamp: new Date().toISOString(),
      usesContext: true
    };
    setMessages((prev) => [...prev, userMessage]);
    setMessagesLoaded(true);
    const messageText = inputValue;
    setInputValue("");

    try {
      const res = await CopilotService.sendMessage(activeSpace.id, messageText, true);

      // Ajouter la réponse de l'assistant
      const assistantMessage = {
        sender: "bot",
        text: res.data.assistant_message.message,
        timestamp: new Date().toISOString(),
        usesContext: true
      };
      setMessages((prev) => [...prev, assistantMessage]);
      try { trackEvent("copilot", "copilot_message_sent", undefined, undefined, { dedupeMs: 1500, sampleRate: 0.25 }); } catch (_) { }
      try { trackEvent("copilot", "reply_received", undefined, undefined, { dedupeMs: 1500, sampleRate: 0.1 }); } catch (_) { }

      // Rafraîchir les crédits
      await refreshCredits();

    } catch (err) {
      console.error("Erreur lors de l'envoi du message", err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      if (err?.response?.status === 402 || detail?.toLowerCase?.().includes("crédits insuffisants")) {
        toast.error(detail || "Crédits insuffisants.");
        setShowCreditsDialog(true);
      } else {
        toast.error("Erreur lors de l'envoi du message");
      }
      try { trackEvent("copilot", "send_exception"); } catch (_) { }
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };



  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Upload de documents dans un espace existant
  const handleUploadToSpace = async () => {
    if (!activeSpace || uploadedFiles.length === 0) return;

    const needed = calculateCost();
    if ((user?.credits ?? 0) < needed) {
      toast.error("Crédits insuffisants.");
      setShowCreditsDialog(true);
      try { trackEvent("copilot", "upload_blocked_no_credits", undefined, needed); } catch (_) { }
      return;
    }

    // Fermer le dialog immédiatement
    setShowUploadDialog(false);
    const filesToUpload = [...uploadedFiles];
    setUploadedFiles([]);

    // Ajouter les documents en cours d'analyse à l'espace actif
    const analyzingDocuments = filesToUpload.map(file => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      original_filename: file.name,
      file_size: file.size,
      status: 'analyzing',
      created_at: new Date().toISOString()
    }));

    setActiveSpace(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...analyzingDocuments]
    }));

    // Toast de début
    toast.info(`Début de l'analyse de ${filesToUpload.length} document(s)...`);
    try { trackEvent("copilot", "upload_started", undefined, filesToUpload.length); } catch (_) { }

    // Upload en arrière-plan (non-bloquant)
    filesToUpload.forEach((file) => {
      CopilotService.uploadDocument(activeSpace.id, file)
        .then((result) => {
          toast.success(`${file.name} analysé avec succès !`);
          try { trackEvent("copilot", "doc_uploaded"); } catch (_) { }
        })
        .catch((err) => {
          console.error(`Erreur lors de l'upload de ${file.name}:`, err);
          toast.error(`Erreur lors de l'analyse de ${file.name}`);
          try { trackEvent("copilot", "doc_upload_error"); } catch (_) { }
        });
    });

    // Recharger l'espace après un délai pour laisser le temps à l'analyse
    setTimeout(async () => {
      try {
        const spaceRes = await CopilotService.getSpace(activeSpace.id);
        setActiveSpace(spaceRes.data);
        // Rafraîchir les crédits
        await refreshCredits();
      } catch (err) {
        console.error("Erreur lors du rechargement de l'espace", err);
      }
    }, 3000);
  };

  const handleSelectSpace = (space) => {
    if (activeSpace?.id === space.id) {
      navigate(`/copilot/${encodeId(space.id)}`);
      if (window.innerWidth < 1024) setSidebarOpen(false);
      return;
    }
    setMessages([]);
    navigate(`/copilot/${encodeId(space.id)}`);
    try { trackEvent("copilot", "select_space"); } catch (_) { }
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const handleClickLogOut = async (e) => {
    e.preventDefault();
    try {
      await DataService.logout();
      resetUserInfo();
      navigate(buildLoginUrlFromLocation(location), { replace: true, state: { from: location } });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteSpace = async (spaceId) => {
    try {
      await CopilotService.deleteSpace(spaceId);
      await fetchSpaces();
      if (activeSpace?.id === spaceId) {
        setActiveSpace(null);
        setMessages([]);
        navigate(RouterPath.COPILOT, { replace: true });
      }
      toast.success("Espace supprimé avec succès");
    } catch (err) {
      console.error("Erreur lors de la suppression de l'espace", err);
      toast.error("Erreur lors de la suppression de l'espace");
    }
  };

  const confirmDeleteSpace = (space) => {
    setItemToDelete(space);
    setShowDeleteSpaceDialog(true);
  };

  const handleDeleteDocument = async (documentId) => {
    if (!activeSpace) return;

    try {
      // Supprimer visuellement le document immédiatement
      setActiveSpace(prev => ({
        ...prev,
        documents: prev.documents.filter(doc => doc.id !== documentId)
      }));

      // Supprimer le document en arrière-plan
      await CopilotService.deleteDocument(documentId);

      toast.success("Document supprimé avec succès");
    } catch (err) {
      console.error("Erreur lors de la suppression du document", err);
      toast.error("Erreur lors de la suppression du document");

      // En cas d'erreur, remettre le document dans la liste
      if (activeSpace) {
        const spaceRes = await CopilotService.getSpace(activeSpace.id);
        setActiveSpace(spaceRes.data);
      }
    }
  };

  const confirmDeleteDocument = (document) => {
    setItemToDelete(document);
    setShowDeleteDocumentDialog(true);
  };

  const handleRetryDocument = async (document) => {
    try {
      toast.info(`Nouvelle tentative d'analyse pour ${document.original_filename}...`);

      const result = await CopilotService.retryDocument(document.id);

      if (result.data) {
        toast.success(`${document.original_filename} réanalysé avec succès !`);

        // Recharger l'espace pour mettre à jour le statut
        if (activeSpace) {
          const spaceRes = await CopilotService.getSpace(activeSpace.id);
          setActiveSpace(spaceRes.data);
        }
      }
    } catch (err) {
      console.error(`Erreur lors du retry de ${document.original_filename}:`, err);
      toast.error(`Erreur lors de la nouvelle tentative pour ${document.original_filename}`);
    }
  };

  const displayName = user ? user.first_name : null;

  const suggestedPrompts = [
    "De quoi parle le document ?",
    "Liste moi les critères d'éligibilité",
    "Quels sont les points importants à retenir ?"
  ];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="flex h-screen relative app-page-gradient">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Bouton hamburger flottant pour mobile */}
      {isMobile && !sidebarOpen && (
        <Button
          onClick={() => setSidebarOpen((prev) => !prev)}
          variant="secondary"
          size="sm"
          title="Ouvrir la barre latérale"
          className="fixed top-4 left-4 z-50 shadow-lg border-0 bg-card/90 backdrop-blur-sm hover:bg-card text-foreground"
        >
          <Menu className="w-4 h-4" />
        </Button>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed lg:relative z-40 transition-all duration-300 ease-in-out bg-card/95 backdrop-blur-sm flex flex-col overflow-hidden h-full",
        sidebarOpen ? 'w-60 translate-x-0' : 'w-60 -translate-x-full lg:translate-x-0 lg:w-16',
        "lg:border-r lg:border-border/30"
      )}>
        {/* Brand header */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={peppaLogo} alt="PeppaCorp Logo" className="w-40 rounded-sm" />
                {/* <span className="text-sm font-semibold text-foreground">Copilote</span> */}
              </div>
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="icon"
                title="Replier la barre latérale"
                className="hidden lg:inline-flex hover:bg-accent/50 text-foreground"
                aria-label="Replier la barre latérale"
              >
                <Menu className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="sm"
                className="lg:hidden hover:bg-accent/50 text-foreground"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <button
                aria-label="Ouvrir la barre latérale"
                className="group relative rounded hover:bg-accent/40 text-foreground"
                title="Ouvrir la barre latérale"
                onClick={() => setSidebarOpen(true)}
              >
                <img
                  src="/favicon.svg"
                  alt="PeppaCorp"
                  className="h-6 w-6 transition-opacity duration-150 group-hover:opacity-0"
                />
                <Menu className="absolute inset-0 m-auto h-4 w-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation vers le chat */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Button
              onClick={() => navigate(RouterPath.CHAT)}
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <CircleChevronLeft className="w-4 h-4" />
              <span>Retour</span>
            </Button>
          ) : (
            <div className="flex items-center justify-center">
              <Button
                onClick={() => navigate(RouterPath.CHAT)}
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                title="Retour"
                aria-label="Retour"
              >
                <CircleChevronLeft className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Nouvel espace */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
                  variant="ghost"
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Nouvel espace</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[80vh] bg-background text-foreground border-border flex flex-col">
                <DialogHeader>
                  <DialogTitle className="text-foreground">Créer un nouvel espace de travail</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Ajoutez vos documents clés : appels à projets, aides et règlements, et laissez notre assistant les analyser.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                  <div>
                    <label className="text-sm font-medium text-foreground">Nom de l'espace</label>
                    <Input
                      value={newSpaceName}
                      onChange={(e) => setNewSpaceName(e.target.value)}
                      placeholder="Ex: Appel à projets Innovation 2027"
                      className="mt-1 text-foreground"
                    />
                  </div>
                  <div className="flex-1 flex flex-col min-h-0">
                    <label className="text-sm font-medium text-foreground">Documents</label>
                    <div className="mt-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline"
                        className="w-full text-foreground"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Importer des documents
                      </Button>
                    </div>
                    {uploadedFiles.length > 0 && (
                      <div className="mt-2 flex-1 min-h-0 flex flex-col">
                        {/* <div className="text-sm text-muted-foreground dark:text-muted-foreground mb-2">
                          <span className="inline-flex items-center gap-1">
                            Coût estimé : {calculateCost()}
                            <img src={creditsIcon} alt="crédits" className="inline-block w-3.5 h-3.5" />
                          </span>
                        </div> */}
                        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                          {uploadedFiles.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-accent/20 dark:bg-accent/20 rounded">
                              <div className="flex items-center gap-2 min-w-0 flex-1 max-w-[350px]">
                                <File className="w-4 h-4 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <span className="text-sm truncate block">{file.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatFileSize(file.size)}
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(file.id)}
                                className="flex-shrink-0 ml-2"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="text-foreground" onClick={() => setShowCreateDialog(false)}>
                    Annuler
                  </Button>
                  <Button
                    className="text-foreground"
                    onClick={handleCreateSpace}
                    disabled={!newSpaceName.trim() || isLoading}
                  >
                    {isLoading ? "Création en cours..." : (
                      <span className="inline-flex items-center gap-1">
                        Créer l'espace
                      </span>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : (
            <div className="flex items-center justify-center">
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-accent/50 text-foreground"
                    title="Nouvel espace"
                    aria-label="Nouvel espace"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] bg-background text-foreground border-border flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Créer un nouvel espace de candidature</DialogTitle>
                    <DialogDescription>
                      Nommez votre espace et importez vos documents pour commencer l'analyse.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Nom de l'espace</label>
                      <Input
                        value={newSpaceName}
                        onChange={(e) => setNewSpaceName(e.target.value)}
                        placeholder="Ex: Appel à projets Innovation 2024"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Documents</label>
                      <div className="mt-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Importer des documents
                        </Button>
                      </div>
                      {uploadedFiles.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {uploadedFiles.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-accent/20 rounded">
                              <div className="flex items-center gap-2">
                                <File className="w-4 h-4" />
                                <span className="text-sm">{file.name}</span>
                                <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(file.id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          {/* <div className="text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              Coût estimé : {calculateCost()}
                              <img src={creditsIcon} alt="crédits" className="inline-block w-3.5 h-3.5" />
                            </span>
                          </div> */}
                        </div>
                      )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Annuler
                    </Button>
                    <Button
                      onClick={handleCreateSpace}
                      disabled={!newSpaceName.trim() || isLoading}
                    >
                      {isLoading ? "Création en cours..." : (
                        <span className="inline-flex items-center gap-1">
                          Créer l'espace
                        </span>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Liste des espaces */}
        <div className="flex-1 overflow-y-auto p-2 sidebar-scroll">
          {!sidebarOpen && !isMobile ? null : !spacesLoaded ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : spaces.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-foreground">Aucun espace de travail</p>
              <p className="text-xs text-muted-foreground mt-1">Créez votre premier espace pour commencer</p>
            </div>
          ) : (
            <div className="space-y-2">
              {spaces.map((space) => (
                <Card
                  key={space.id}
                  className={cn(
                    "cursor-pointer transition-all duration-200 group shadow-none border-0",
                    activeSpace?.id === space.id
                      ? "bg-accent/60 ring-1 ring-primary/20"
                      : "hover:bg-accent/20 bg-transparent"
                  )}
                  onClick={() => handleSelectSpace(space)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm font-medium truncate",
                          activeSpace?.id === space.id
                            ? "text-primary"
                            : "text-foreground"
                        )}>
                          {space.name}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {new Date(space.last_activity).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-accent/50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        aria-label="Supprimer l'espace"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteSpace(space);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-border/20">
          <div className="flex items-center justify-between mb-3">
            <ThemeToggle compact={!sidebarOpen} />
            {/* {sidebarOpen && (
              <Badge variant="secondary" className="text-xs bg-accent/50 text-foreground border-0 inline-flex items-center gap-1">
                {user?.credits ?? 0}
                <img src={creditsIcon} alt="crédits" className="inline-block w-3 h-3" />
              </Badge>
            )} */}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={cn("w-full hover:bg-accent/50 text-foreground", (!sidebarOpen) ? "px-0 justify-center" : "justify-start")}>
                <User className={cn("h-4 w-4", (!sidebarOpen) ? "" : "mr-2")} />
                {sidebarOpen && (
                  <>
                    <span className="truncate text-foreground">
                      {displayName || "Utilisateur"}
                    </span>
                    <ChevronDown className="h-4 w-4 ml-auto" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(RouterPath.PROFILE)}>
                <User className="mr-2 h-4 w-4" />
                <span>Mon profil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(RouterPath.BILLING)}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span className="inline-flex items-center gap-1">Abonnement</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleClickLogOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Se déconnecter</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Zone principale */}
      <div className="flex-1 flex flex-col relative min-w-0 min-h-0 pt-16 lg:pt-0">
        {/* En mobile, on met en avant le chat et on ouvre les documents dans un panneau dédié */}
        {isMobile && activeSpace && (
          <div className="fixed top-4 left-1/2 z-40 -translate-x-1/2">
            <Button
              size="sm"
              variant="secondary"
              className="shadow-lg border-0 bg-card/90 backdrop-blur-sm hover:bg-card text-foreground"
              onClick={() => setShowMobileDocsPanel(true)}
            >
              <FileText className="w-4 h-4 mr-2" />
              <span>Documents & synthèse</span>
            </Button>
          </div>
        )}

        {activeSpace ? (
          <div className="flex flex-col lg:flex-row h-full min-h-0 overflow-hidden">
            {/* Colonne gauche : Gestion documentaire (masquée en mobile, accessible via le bouton flottant) */}
            <div className="hidden w-full lg:flex lg:w-1/3 border-b lg:border-b-0 lg:border-r border-border/20 flex-col min-h-0">
              {/* En-tête */}
              <div className="p-4 border-b border-border/20">
                <h2 className="text-lg font-semibold text-foreground">{activeSpace.name}</h2>
                <p className="text-sm text-muted-foreground">Documents analysés</p>
              </div>

              {/* Guide généré */}
              {activeSpace.summary && (
                <div className="p-4 border-b border-border/20 bg-accent/10">
                  <h3 className="text-sm font-medium text-foreground mb-3">Guide généré</h3>
                  <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                    {(() => {
                      try {
                        const summary = JSON.parse(activeSpace.summary);
                        return (
                          <>
                            {summary.resume_reglement && summary.resume_reglement.trim() && (
                              <div className="border-l-2 border-blue-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Résumé du règlement</div>
                                <p className="text-xs text-muted-foreground">{summary.resume_reglement}</p>
                              </div>
                            )}
                            {summary.conditions_principales && summary.conditions_principales.length > 0 && (
                              <div className="border-l-2 border-green-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Conditions principales</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.conditions_principales.map((condition, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-green-500 mt-0.5">•</span>
                                      <span>{condition}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {summary.points_vigilance && summary.points_vigilance.length > 0 && (
                              <div className="border-l-2 border-yellow-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Points de vigilance</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.points_vigilance.map((point, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-yellow-500 mt-0.5">⚠</span>
                                      <span>{point}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {summary.pieces_demandees && summary.pieces_demandees.length > 0 && (
                              <div className="border-l-2 border-purple-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Pièces probablement demandées</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.pieces_demandees.map((piece, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-purple-500 mt-0.5">📄</span>
                                      <span>{piece}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {(!summary.resume_reglement || !summary.resume_reglement.trim()) &&
                              summary.conditions_principales.length === 0 &&
                              summary.points_vigilance.length === 0 &&
                              summary.pieces_demandees.length === 0 && (
                                <div className="text-xs text-muted-foreground italic">
                                  Aucune information structurée trouvée dans ce document
                                </div>
                              )}
                          </>
                        );
                      } catch (e) {
                        return (
                          <div className="text-xs text-muted-foreground">
                            Analyse en cours...
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Liste des documents */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {!activeSpace.documents || activeSpace.documents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center p-6 border border-dashed border-border/40 rounded-lg bg-muted/30">
                      <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                      <div className="text-sm text-foreground font-medium mb-1">
                        Aucune source ajoutée pour l’instant
                      </div>
                      <div className="text-sm text-muted-foreground mb-3 max-w-md">
                        Importez des documents (PDF, Word, TXT). Le copilote analysera leur contenu pour répondre à vos questions avec des références précises.
                      </div>
                      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setShowUploadDialog(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Ajouter un document
                      </Button>
                    </div>
                  ) : null}
                  {activeSpace.documents && activeSpace.documents.map((doc) => (
                    <Card key={doc.id} className="p-3 border-muted">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.status === 'analyzed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : doc.status === 'analyzing' ? (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRetryDocument(doc)}
                              className="relative h-5 w-5 flex items-center justify-center group rounded hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                              aria-label="Réessayer l'analyse du document"
                              title="Réessayer"
                            >
                              <AlertCircle className="w-4 h-4 text-red-500 transition-opacity duration-150 group-hover:opacity-0" />
                              <RotateCcw className="w-4 h-4 text-red-600 absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                            </button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-accent/50"
                            onClick={() => confirmDeleteDocument(doc)}
                            aria-label="Supprimer le document"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Bouton ajouter document */}
              {activeSpace.documents && activeSpace.documents.length > 0 && (
                <div className="p-4 border-t border-border/20">
                  <Button
                    variant="outline"
                    className="w-full text-foreground"
                    size="sm"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un document
                  </Button>
                </div>
              )}
            </div>

            {/* Colonne droite : Chat intelligent */}

            <div className="flex-1 flex flex-col w-full lg:w-2/3 min-h-0">
              {/* En-tête du chat */}
              <div className="p-4 border-b border-border/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Assistant IA</h3>
                    <p className="text-sm text-muted-foreground">Posez vos questions sur vos documents</p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4">
                {spaceId && !messagesLoaded ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-1/5" />
                        <Skeleton className="h-16 w-3/4" />
                      </div>
                    ))}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Target className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Votre copilote de financement
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md">
                      Je suis là pour vous aider à analyser vos documents et optimiser votre candidature.
                    </p>

                    {/* Prompts suggérés */}
                    <div className="space-y-2 w-full max-w-md">
                      {suggestedPrompts.map((prompt, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          className="w-full justify-start h-auto p-3 text-left text-foreground"
                          onClick={() => setInputValue(prompt)}
                        >
                          <Lightbulb className="w-4 h-4 mr-2 text-primary" />
                          <span className="text-sm">{prompt}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {messages.map((msg, idx) => (
                      <MessageBubble
                        key={idx}
                        message={msg}
                        isLast={idx === messages.length - 1}
                      />
                    ))}

                    {isTyping && <TypingIndicator />}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <Card className="border-t border-border/20 bg-card/80 backdrop-blur-sm shadow-sm">
                <CardContent className="p-4">
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Posez votre question sur vos documents..."
                      disabled={isLoading}
                      className="min-h-[44px] max-h-[200px] pr-12 resize-none input-enhanced border-border/30 focus:border-primary/50"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputValue.trim()}
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 hover:bg-primary/90"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 text-center">
                    L'IA peut commettre des erreurs. Il est recommandé de vérifier les informations importantes.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Dashboard principal */
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <Target className="w-10 h-10 text-primary" />
            </div>

            <h2 className="text-3xl font-bold text-foreground mb-3">
              Copilote de <span className="text-primary">candidature</span>
            </h2>

            <p className="text-foreground max-w-md mb-8 text-lg opacity-90">
              Chargez vos aides et règlements, notre assistant vous aide à y voir clair.
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg">
              {[
                { icon: <FileText className="w-6 h-6" />, title: "Vos documents au clair", desc: "Importez aides, appels à projets et règlements en un clic." },
                { icon: <Target className="w-6 h-6" />, title: "Infos essentielles instantanées", desc: "L’IA repère ce qui compte vraiment dans vos dossiers." },
                { icon: <MessageSquare className="w-6 h-6" />, title: "Réponses contextuelles", desc: "Posez vos questions et obtenez des réponses précises et pertinentes." },
                { icon: <Clock className="w-6 h-6" />, title: "Gain de temps garanti", desc: "Accédez immédiatement aux informations clés sans chercher." }
              ].map((feature, index) => (
                <Card
                  key={index}
                  className="p-4 border-muted bg-card/50 backdrop-blur-sm shadow-sm "
                >
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mb-2 mx-auto transition-transform duration-300">
                    <div className="text-primary-foreground">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="font-semibold text-primary text-sm mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground opacity-80 ">
                    {feature.desc}
                  </p>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-xs text-foreground opacity-70">
              <p>🚀 Créez ou sélectionnez votre espace de travail depuis le panneau latéral pour commencer</p>
            </div>
          </div>
        )}
      </div>

      {/* Panneau mobile pour la gestion des documents et la synthèse */}
      {isMobile && activeSpace && showMobileDocsPanel && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setShowMobileDocsPanel(false)}
          />
          <div className="fixed inset-x-0 top-0 bottom-0 z-50 flex flex-col bg-background">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-foreground truncate max-w-[70vw]">
                  {activeSpace.name}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Documents analysés & guide généré
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                onClick={() => setShowMobileDocsPanel(false)}
                aria-label="Fermer le panneau documents"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Guide généré */}
              {activeSpace.summary && (
                <div className="p-4 border-b border-border/20 bg-accent/10">
                  <h3 className="text-sm font-medium text-foreground mb-3">Guide généré</h3>
                  <div className="space-y-3">
                    {(() => {
                      try {
                        const summary = JSON.parse(activeSpace.summary);
                        return (
                          <>
                            {summary.resume_reglement && summary.resume_reglement.trim() && (
                              <div className="border-l-2 border-blue-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Résumé du règlement</div>
                                <p className="text-xs text-muted-foreground">{summary.resume_reglement}</p>
                              </div>
                            )}
                            {summary.conditions_principales && summary.conditions_principales.length > 0 && (
                              <div className="border-l-2 border-green-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Conditions principales</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.conditions_principales.map((condition, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-green-500 mt-0.5">•</span>
                                      <span>{condition}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {summary.points_vigilance && summary.points_vigilance.length > 0 && (
                              <div className="border-l-2 border-yellow-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Points de vigilance</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.points_vigilance.map((point, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-yellow-500 mt-0.5">⚠</span>
                                      <span>{point}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {summary.pieces_demandees && summary.pieces_demandees.length > 0 && (
                              <div className="border-l-2 border-purple-500/20 pl-3">
                                <div className="text-xs font-medium text-foreground mb-1">Pièces probablement demandées</div>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                  {summary.pieces_demandees.map((piece, index) => (
                                    <li key={index} className="flex items-start gap-1">
                                      <span className="text-purple-500 mt-0.5">📄</span>
                                      <span>{piece}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      } catch (e) {
                        return (
                          <div className="text-xs text-muted-foreground">
                            Analyse en cours...
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Liste des documents */}
              <div className="p-4 space-y-3">
                {!activeSpace.documents || activeSpace.documents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center p-6 border border-dashed border-border/40 rounded-lg bg-muted/30">
                    <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                    <div className="text-sm text-foreground font-medium mb-1">
                      Aucune source ajoutée pour l’instant
                    </div>
                    <div className="text-sm text-muted-foreground mb-3 max-w-md">
                      Importez des documents (PDF, Word, TXT). Le copilote analysera leur contenu pour répondre à vos questions avec des références précises.
                    </div>
                    <Button size="sm" variant="outline" className="text-foreground" onClick={() => {
                      setShowUploadDialog(true);
                      setShowMobileDocsPanel(false);
                    }}>
                      <Upload className="w-4 h-4 mr-2" />
                      Ajouter un document
                    </Button>
                  </div>
                ) : (
                  <>
                    {activeSpace.documents.map((doc) => (
                      <Card key={doc.id} className="p-3 border-muted">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-foreground truncate">{doc.original_filename}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {doc.status === 'analyzed' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : doc.status === 'analyzing' ? (
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRetryDocument(doc)}
                                className="relative h-5 w-5 flex items-center justify-center group rounded hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                aria-label="Réessayer l'analyse du document"
                                title="Réessayer"
                              >
                                <AlertCircle className="w-4 h-4 text-red-500 transition-opacity duration-150 group-hover:opacity-0" />
                                <RotateCcw className="w-4 h-4 text-red-600 absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                              </button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-accent/50"
                              onClick={() => confirmDeleteDocument(doc)}
                              aria-label="Supprimer le document"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}

                    {/* Bouton ajouter document */}
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        className="w-full text-foreground"
                        size="sm"
                        onClick={() => {
                          setShowUploadDialog(true);
                          setShowMobileDocsPanel(false);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un document
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Dialog pour upload de documents dans un espace existant */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] bg-background text-foreground border-border flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground">Ajouter des documents à {activeSpace?.name}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Sélectionnez les documents à analyser pour cet espace de candidature.
            </DialogDescription>
          </DialogHeader>

          {/* Zone de drag & drop */}
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors flex-shrink-0",
                "border-border/50 hover:border-primary/50 bg-background/50 dark:bg-background/50",
                uploadedFiles.length > 0 && "border-primary/50 bg-primary/5 dark:bg-primary/5"
              )}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("border-primary/50", "bg-primary/5");
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-primary/50", "bg-primary/5");
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-primary/50", "bg-primary/5");
                const files = Array.from(e.dataTransfer.files);
                setUploadedFiles(prev => [...prev, ...files]);
              }}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2 dark:text-muted-foreground">
                Glissez-déposez vos documents ici ou cliquez pour sélectionner
              </p>
              <Button
                variant="outline"
                size="sm"
                className="text-foreground"
                onClick={() => document.getElementById('file-input-upload').click()}
              >
                Sélectionner des fichiers
              </Button>
              <input
                id="file-input-upload"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setUploadedFiles(prev => [...prev, ...files]);
                }}
              />
            </div>

            {/* Liste des fichiers sélectionnés */}
            {uploadedFiles.length > 0 && (
              <div className="flex-1 min-h-0 flex flex-col">
                <h4 className="text-sm font-medium text-foreground flex-shrink-0">Fichiers sélectionnés :</h4>
                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-accent/20 dark:bg-accent/20 rounded">
                      <div className="flex items-center gap-2 min-w-0 flex-1 max-w-[350px]">
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <span className="text-sm truncate block">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-accent/50 flex-shrink-0 ml-2"
                        onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowUploadDialog(false)}>
              Annuler
            </Button>
            <Button
              className="text-foreground"
              onClick={handleUploadToSpace}
              disabled={uploadedFiles.length === 0 || isLoading}
            >
              {isLoading ? "Upload en cours..." : (
                <span className="inline-flex items-center gap-1">
                  Ajouter
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation pour suppression d'espace */}
      <Dialog open={showDeleteSpaceDialog} onOpenChange={setShowDeleteSpaceDialog}>
        <DialogContent className="sm:max-w-[400px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer l'espace "{itemToDelete?.name}" ?
              Cette action supprimera définitivement l'espace et tous ses documents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowDeleteSpaceDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (itemToDelete) {
                  handleDeleteSpace(itemToDelete.id);
                  setShowDeleteSpaceDialog(false);
                  setItemToDelete(null);
                }
              }}
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation pour suppression de document */}
      <Dialog open={showDeleteDocumentDialog} onOpenChange={setShowDeleteDocumentDialog}>
        <DialogContent className="sm:max-w-[400px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer le document
              {" "}
              <span className="break-words break-all inline-block max-w-full align-baseline">
                "{itemToDelete?.original_filename}"
              </span>
              {" "}? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowDeleteDocumentDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (itemToDelete) {
                  handleDeleteDocument(itemToDelete.id);
                  setShowDeleteDocumentDialog(false);
                  setItemToDelete(null);
                }
              }}
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog crédits insuffisants */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent className="sm:max-w-[420px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Crédits insuffisants</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Vous n'avez pas assez de crédits pour effectuer cette action. Rendez-vous sur la page d'achat pour recharger votre solde.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowCreditsDialog(false)}>
              Plus tard
            </Button>
            <Button
              onClick={() => {
                setShowCreditsDialog(false);
                navigate(RouterPath.BILLING);
              }}
            >
              Abonnement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
